// API Service for Destiny Jr Staff
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fetch with retry - reads body eagerly to prevent SW body consumption issues
const safeFetch = async (url, options, retries = 2) => {
  for (let i = 0; i <= retries; i++) {
    try {
      const resp = await fetch(url, options);
      // Try to read body, but gracefully handle if SW consumed it
      let text = '';
      try {
        text = await resp.text();
      } catch {
        // Body might be consumed by service worker - still return status
      }
      return { ok: resp.ok, status: resp.status, text };
    } catch (err) {
      // Only retry on actual network failures (fetch itself threw)
      if (i === retries) {
        throw new Error('Gagal menyambung ke pelayan. Sila semak sambungan internet anda.');
      }
      await new Promise(r => setTimeout(r, 300 * (i + 1)));
    }
  }
};

// Parse response - works with pre-read body
const parseResponse = (result) => {
  if (!result.ok) {
    let errorDetail = '';
    if (result.text) {
      try {
        const parsed = JSON.parse(result.text);
        errorDetail = parsed.detail || parsed.message || '';
      } catch {
        if (result.text.length < 200) errorDetail = result.text;
      }
    }
    if (!errorDetail) {
      if (result.status === 401) errorDetail = 'Email atau kata laluan tidak sah';
      else if (result.status === 403) errorDetail = 'Akses tidak dibenarkan';
      else if (result.status === 404) errorDetail = 'Data tidak dijumpai';
      else errorDetail = `Ralat pelayan (${result.status})`;
    }
    throw new Error(errorDetail);
  }
  return result.text ? JSON.parse(result.text) : {};
};

// Get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Compatibility: handleResponse wrapper for remaining API functions
// These use the Response-like object from safeFetch
const handleResponse = async (resultOrResponse) => {
  // If it's already our safeFetch result (has .text string), use parseResponse
  if (typeof resultOrResponse.text === 'string' && 'ok' in resultOrResponse) {
    return parseResponse(resultOrResponse);
  }
  // Fallback for raw Response objects (e.g. blob downloads)
  if (!resultOrResponse.ok) {
    let errorDetail = `Ralat pelayan (${resultOrResponse.status})`;
    try {
      const text = await resultOrResponse.text();
      const parsed = JSON.parse(text);
      errorDetail = parsed.detail || parsed.message || errorDetail;
    } catch { /* ignore */ }
    throw new Error(errorDetail);
  }
  return resultOrResponse.json();
};

// Simple axios-like wrapper for easy API calls
const api = {
  get: async (url) => {
    const result = await safeFetch(`${API_URL}/api${url}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return { data: parseResponse(result) };
  },
  post: async (url, data = {}) => {
    const result = await safeFetch(`${API_URL}/api${url}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return { data: parseResponse(result) };
  },
  put: async (url, data = {}) => {
    const result = await safeFetch(`${API_URL}/api${url}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return { data: parseResponse(result) };
  },
  delete: async (url) => {
    const result = await safeFetch(`${API_URL}/api${url}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return { data: parseResponse(result) };
  }
};

export default api;

// Auth API
export const authAPI = {
  register: async (email, password, name, role = 'staff') => {
    const result = await safeFetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, role })
    });
    const data = parseResponse(result);
    return data;
  },

  login: async (email, password) => {
    const result = await safeFetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = parseResponse(result);
    localStorage.setItem('session_token', data.access_token);
    return data;
  },

  googleOAuth: async (sessionId) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const result = await safeFetch(`${API_URL}/api/auth/google/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId })
    });
    const data = parseResponse(result);
    localStorage.setItem('session_token', data.access_token);
    return data;
  },

  me: async () => {
    const result = await safeFetch(`${API_URL}/api/auth/me`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return parseResponse(result);
  },

  logout: async () => {
    try {
      await safeFetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: getAuthHeaders()
      }, 0);
    } catch (e) {
      // Ignore logout errors
    }
    localStorage.removeItem('session_token');
    localStorage.removeItem('destiny_user');
  },

  getPendingRegistrations: async () => {
    const response = await safeFetch(`${API_URL}/api/auth/pending-registrations`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  approveRegistration: async (userId) => {
    const response = await safeFetch(`${API_URL}/api/auth/approve-registration/${userId}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  rejectRegistration: async (userId) => {
    const response = await safeFetch(`${API_URL}/api/auth/reject-registration/${userId}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Users API
export const usersAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/users${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getById: async (userId) => {
    const response = await safeFetch(`${API_URL}/api/users/${userId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  create: async (userData) => {
    const response = await safeFetch(`${API_URL}/api/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData)
    });
    return handleResponse(response);
  },

  update: async (userId, updates) => {
    const response = await safeFetch(`${API_URL}/api/users/${userId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });
    return handleResponse(response);
  },

  getDeleted: async () => {
    const response = await safeFetch(`${API_URL}/api/users/deleted`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  restoreDeleted: async (userId) => {
    const response = await safeFetch(`${API_URL}/api/users/deleted/${userId}/restore`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  delete: async (userId) => {
    const response = await safeFetch(`${API_URL}/api/users/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Branches API
export const branchesAPI = {
  getAll: async () => {
    const response = await safeFetch(`${API_URL}/api/branches`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getById: async (branchId) => {
    const response = await safeFetch(`${API_URL}/api/branches/${branchId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await safeFetch(`${API_URL}/api/branches`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  update: async (branchId, updates) => {
    const response = await safeFetch(`${API_URL}/api/branches/${branchId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });
    return handleResponse(response);
  },

  delete: async (branchId) => {
    const response = await safeFetch(`${API_URL}/api/branches/${branchId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  testWhatsApp: async (branchId) => {
    const response = await safeFetch(`${API_URL}/api/branches/${branchId}/test-whatsapp`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Attendance API
export const attendanceAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/attendance${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getMyToday: async () => {
    const response = await safeFetch(`${API_URL}/api/attendance/my-today`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  clockIn: async (branchId, latitude, longitude, verificationMethod) => {
    const response = await safeFetch(`${API_URL}/api/attendance/clock-in`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ branch_id: branchId, latitude, longitude, verification_method: verificationMethod })
    });
    return handleResponse(response);
  },

  clockOut: async (latitude, longitude, verificationMethod, isHalfDay = false) => {
    const response = await safeFetch(`${API_URL}/api/attendance/clock-out`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ latitude, longitude, verification_method: verificationMethod, is_half_day: isHalfDay })
    });
    return handleResponse(response);
  },

  getStats: async (date, branchId) => {
    const params = {};
    if (date) params.date = date;
    if (branchId) params.branch_id = branchId;
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/attendance/stats${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getMyDeficit: async () => {
    const response = await safeFetch(`${API_URL}/api/attendance/my-deficit`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getOpenSessions: async () => {
    const response = await safeFetch(`${API_URL}/api/attendance/open-sessions`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  bulkCloseStale: async (cutoffDate, staffIds) => {
    const body = {};
    if (cutoffDate) body.cutoff_date = cutoffDate;
    if (staffIds) body.staff_ids = staffIds;
    const response = await safeFetch(`${API_URL}/api/attendance/bulk-close-stale`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body)
    });
    return handleResponse(response);
  }
};

// Leave API
export const leaveAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/leave${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getMyBalance: async () => {
    const response = await safeFetch(`${API_URL}/api/leave/my-balance`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await safeFetch(`${API_URL}/api/leave`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  updateStatus: async (leaveId, status, rejectionReason) => {
    const response = await safeFetch(`${API_URL}/api/leave/${leaveId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status, rejection_reason: rejectionReason })
    });
    return handleResponse(response);
  },

  cancel: async (leaveId) => {
    const response = await safeFetch(`${API_URL}/api/leave/${leaveId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getBalance: async (staffId) => {
    const response = await safeFetch(`${API_URL}/api/leave/balance/${staffId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateBalance: async (staffId, balanceData) => {
    const response = await safeFetch(`${API_URL}/api/leave/balance/${staffId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(balanceData)
    });
    return handleResponse(response);
  },

  getBalanceHistory: async (staffId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/leave/balance-history/${staffId}${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getAllBalanceHistory: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/leave/balance-history-all${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Audit API
export const auditAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/audit${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getStats: async () => {
    const response = await safeFetch(`${API_URL}/api/audit/stats`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  clear: async () => {
    const response = await safeFetch(`${API_URL}/api/audit`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Location Tracking API
export const locationAPI = {
  updateLocation: async (latitude, longitude, accuracy) => {
    const response = await safeFetch(`${API_URL}/api/location/update`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ latitude, longitude, accuracy })
    });
    return handleResponse(response);
  },

  batchUpdate: async (locations) => {
    const response = await safeFetch(`${API_URL}/api/location/batch-update`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ locations })
    });
    return handleResponse(response);
  },

  getLive: async (branchId) => {
    const params = branchId ? `?branch_id=${branchId}` : '';
    const response = await safeFetch(`${API_URL}/api/location/live${params}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getHistory: async (staffId, date) => {
    const params = date ? `?date=${date}` : '';
    const response = await safeFetch(`${API_URL}/api/location/history/${staffId}${params}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getHistoryAll: async (date, branchId) => {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (branchId) params.append('branch_id', branchId);
    const query = params.toString();
    const response = await safeFetch(`${API_URL}/api/location/history-all${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getStats: async (date, branchId) => {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (branchId) params.append('branch_id', branchId);
    const query = params.toString();
    const response = await safeFetch(`${API_URL}/api/location/stats${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Biometric API (Face ID / Fingerprint)
export const biometricAPI = {
  getStatus: async () => {
    const response = await safeFetch(`${API_URL}/api/biometric/status`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  registerStart: async () => {
    const response = await safeFetch(`${API_URL}/api/biometric/register/start`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  registerComplete: async (credentialData) => {
    const response = await safeFetch(`${API_URL}/api/biometric/register/complete`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(credentialData)
    });
    return handleResponse(response);
  },

  authStart: async () => {
    const response = await safeFetch(`${API_URL}/api/biometric/auth/start`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  authComplete: async (authData) => {
    const response = await safeFetch(`${API_URL}/api/biometric/auth/complete`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(authData)
    });
    return handleResponse(response);
  },

  login: async (userId, credentialId) => {
    const response = await safeFetch(`${API_URL}/api/biometric/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, credential_id: credentialId })
    });
    return handleResponse(response);
  },

  remove: async () => {
    const response = await safeFetch(`${API_URL}/api/biometric/remove`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Payslip API
export const payslipAPI = {
  getSettings: async (staffId) => {
    const response = await safeFetch(`${API_URL}/api/payslip/settings/${staffId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateSettings: async (staffId, settings) => {
    const response = await safeFetch(`${API_URL}/api/payslip/settings/${staffId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(settings)
    });
    return handleResponse(response);
  },

  resetSettings: async (staffId) => {
    const response = await safeFetch(`${API_URL}/api/payslip/settings/${staffId}/reset`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  generate: async (staffId, month, year) => {
    const response = await safeFetch(`${API_URL}/api/payslip/generate/${staffId}?month=${month}&year=${year}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  generateAll: async (month, year) => {
    const response = await safeFetch(`${API_URL}/api/payslip/generate-all?month=${month}&year=${year}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getPending: async () => {
    const response = await safeFetch(`${API_URL}/api/payslip/all/pending`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getByStaff: async (staffId, year) => {
    const params = year ? `?year=${year}` : '';
    const response = await safeFetch(`${API_URL}/api/payslip/${staffId}${params}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getDetail: async (payslipId) => {
    const response = await safeFetch(`${API_URL}/api/payslip/detail/${payslipId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  submit: async (payslipId) => {
    const response = await safeFetch(`${API_URL}/api/payslip/submit/${payslipId}`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  approve: async (payslipId) => {
    const response = await safeFetch(`${API_URL}/api/payslip/approve/${payslipId}`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  release: async (payslipId) => {
    const response = await safeFetch(`${API_URL}/api/payslip/release/${payslipId}`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  reject: async (payslipId, reason) => {
    const response = await safeFetch(`${API_URL}/api/payslip/reject/${payslipId}?reason=${encodeURIComponent(reason)}`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  delete: async (payslipId) => {
    const response = await safeFetch(`${API_URL}/api/payslip/${payslipId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  deleteRequest: async (requestId) => {
    const response = await safeFetch(`${API_URL}/api/payslip/requests/${requestId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Generation tracking
  getTracking: async (month, year) => {
    const response = await safeFetch(`${API_URL}/api/payslip/tracking/status?month=${month}&year=${year}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Staff request flow
  requestPayslip: async (month, year, remarks) => {
    const response = await safeFetch(`${API_URL}/api/payslip/request`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ month, year, remarks: remarks || null })
    });
    return handleResponse(response);
  },

  getRequests: async () => {
    const response = await safeFetch(`${API_URL}/api/payslip/requests/all`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getMyRequests: async () => {
    const response = await safeFetch(`${API_URL}/api/payslip/requests/mine`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  approveRequest: async (requestId) => {
    const response = await safeFetch(`${API_URL}/api/payslip/requests/${requestId}/approve`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  rejectRequest: async (requestId) => {
    const response = await safeFetch(`${API_URL}/api/payslip/requests/${requestId}/reject`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Staff salary check
  checkMySalary: async () => {
    const response = await safeFetch(`${API_URL}/api/payslip/salary-check/me`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Staff payslip preview
  previewMyPayslip: async (month, year) => {
    const response = await safeFetch(`${API_URL}/api/payslip/preview/me?month=${month}&year=${year}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Bulk approve payslips
  bulkApprove: async (payslipIds) => {
    const response = await safeFetch(`${API_URL}/api/payslip/bulk-approve`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ payslip_ids: payslipIds })
    });
    return handleResponse(response);
  },

  // Bulk approve requests
  bulkApproveRequests: async (requestIds) => {
    const response = await safeFetch(`${API_URL}/api/payslip/bulk-approve-requests`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ request_ids: requestIds })
    });
    return handleResponse(response);
  },

  // Pay date settings
  getPayDateSettings: async () => {
    const response = await safeFetch(`${API_URL}/api/payslip/pay-date-settings`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updatePayDateSettings: async (months) => {
    const response = await safeFetch(`${API_URL}/api/payslip/pay-date-settings`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ months })
    });
    return handleResponse(response);
  }
};

// Reports API
export const reportsAPI = {
  getMonthly: async (year, month, branchId) => {
    const params = { year, month };
    if (branchId) params.branch_id = branchId;
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/reports/monthly?${query}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  exportExcel: async (year, month, branchId, staffId, startDate, endDate) => {
    const params = {};
    if (startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    } else {
      params.year = year;
      params.month = month;
    }
    if (branchId) params.branch_id = branchId;
    if (staffId) params.staff_id = staffId;
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/api/reports/export/excel?${query}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  },

  exportPDF: async (year, month, branchId, staffId, startDate, endDate) => {
    const params = {};
    if (startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    } else {
      params.year = year;
      params.month = month;
    }
    if (branchId) params.branch_id = branchId;
    if (staffId) params.staff_id = staffId;
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/api/reports/export/pdf?${query}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  },

  getTeamAttendance: async (date, branchId) => {
    const params = {};
    if (date) params.date = date;
    if (branchId) params.branch_id = branchId;
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/reports/team-attendance${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getLeaveCalendar: async (year, month, branchId) => {
    const params = { year, month };
    if (branchId) params.branch_id = branchId;
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/reports/leave-calendar?${query}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Overtime Management API
export const overtimeAPI = {
  getOvertimeRequests: async (status, branchId) => {
    const params = {};
    if (status) params.status = status;
    if (branchId) params.branch_id = branchId;
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/overtime${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getUserOvertimeRequests: async (userId) => {
    const response = await safeFetch(`${API_URL}/api/overtime/user/${userId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getPendingOTCount: async () => {
    const response = await safeFetch(`${API_URL}/api/overtime/pending/count`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  approveOvertime: async (otId, status, remarks, approvedBy) => {
    const response = await safeFetch(`${API_URL}/api/overtime/${otId}/approve?approved_by=${approvedBy}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status, remarks })
    });
    return handleResponse(response);
  },

  checkAndTriggerOT: async (attendanceId) => {
    const response = await safeFetch(`${API_URL}/api/overtime/check-ot/${attendanceId}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateOTReason: async (otId, reason, studentName) => {
    const response = await safeFetch(`${API_URL}/api/overtime/${otId}/reason`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason, student_name: studentName })
    });
    return handleResponse(response);
  },

  getNotifications: async (userId, unreadOnly = false) => {
    const params = unreadOnly ? { unread_only: true } : {};
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/overtime/notifications/${userId}${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getUnreadNotificationCount: async (userId) => {
    const response = await safeFetch(`${API_URL}/api/overtime/notifications/${userId}/unread-count`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  markNotificationRead: async (notificationId) => {
    const response = await safeFetch(`${API_URL}/api/overtime/notifications/${notificationId}/read`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  markAllNotificationsRead: async (userId) => {
    const response = await safeFetch(`${API_URL}/api/overtime/notifications/${userId}/read-all`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  deleteOTRequest: async (otId) => {
    const response = await safeFetch(`${API_URL}/api/overtime/${otId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  editOTRecord: async (otId, editorId, data) => {
    const response = await safeFetch(`${API_URL}/api/overtime/${otId}/edit?editor_id=${editorId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  }
};


// Analytics API
export const analyticsAPI = {
  getSummary: async (year, month, branchId) => {
    const params = { year };
    if (month) params.month = month;
    if (branchId) params.branch_id = branchId;
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/analytics/dashboard?${query}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

export const schedulesAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/schedules?${query}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getMyToday: async () => {
    const response = await safeFetch(`${API_URL}/api/schedules/my-today`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await safeFetch(`${API_URL}/api/schedules`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  delete: async (scheduleId) => {
    const response = await safeFetch(`${API_URL}/api/schedules/${scheduleId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  update: async (scheduleId, data) => {
    const response = await safeFetch(`${API_URL}/api/schedules/${scheduleId}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  updateType: async (scheduleId, scheduleType) => {
    const response = await safeFetch(`${API_URL}/api/schedules/${scheduleId}/type`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ schedule_type: scheduleType })
    });
    return handleResponse(response);
  },

  bulkCreate: async (data) => {
    const response = await safeFetch(`${API_URL}/api/schedules/bulk`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  getBulk: async (staffId, year, month) => {
    const response = await safeFetch(`${API_URL}/api/schedules/bulk/${staffId}?year=${year}&month=${month}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getBulkRange: async (staffId, startDate, endDate) => {
    const response = await safeFetch(`${API_URL}/api/schedules/bulk/${staffId}?start_date=${startDate}&end_date=${endDate}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getBulkDateSettings: async () => {
    const response = await safeFetch(`${API_URL}/api/schedules/bulk-date-settings`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  saveBulkDateSettings: async (startDate, endDate) => {
    const response = await safeFetch(`${API_URL}/api/schedules/bulk-date-settings`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ start_date: startDate, end_date: endDate })
    });
    return handleResponse(response);
  }
};

export const notificationsAPI = {
  getAll: async (unreadOnly = false) => {
    const params = unreadOnly ? '?unread_only=true' : '';
    const response = await safeFetch(`${API_URL}/api/notifications${params}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  markRead: async (notificationId) => {
    const response = await safeFetch(`${API_URL}/api/notifications/${notificationId}/read`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  markAllRead: async () => {
    const response = await safeFetch(`${API_URL}/api/notifications/read-all`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getDailySummary: async (date, branchId) => {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (branchId) params.append('branch_id', branchId);
    const query = params.toString();
    const response = await safeFetch(`${API_URL}/api/notifications/daily-summary${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  sendDailySummary: async (branchId) => {
    const params = branchId ? `?branch_id=${branchId}` : '';
    const response = await safeFetch(`${API_URL}/api/notifications/daily-summary/send${params}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Biodata API
export const biodataAPI = {
  getMyBiodata: async () => {
    const response = await safeFetch(`${API_URL}/api/biodata/me`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateMyBiodata: async (data) => {
    const response = await safeFetch(`${API_URL}/api/biodata/me`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/biodata/all${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getById: async (userId) => {
    const response = await safeFetch(`${API_URL}/api/biodata/${userId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  adminUpdate: async (userId, data) => {
    const response = await safeFetch(`${API_URL}/api/biodata/${userId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  }
};


// Settings API (Ramadan etc.)
export const settingsAPI = {
  getRamadan: async (year) => {
    const response = await safeFetch(`${API_URL}/api/settings/ramadan?year=${year}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  saveRamadan: async (data) => {
    const response = await safeFetch(`${API_URL}/api/settings/ramadan`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  detectRamadan: async (year) => {
    const response = await safeFetch(`${API_URL}/api/settings/ramadan/detect?year=${year}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  getTodayWorkStandard: async () => {
    const response = await safeFetch(`${API_URL}/api/settings/today-work-standard`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};


// Face Records API (Admin)
export const faceRecordsAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await safeFetch(`${API_URL}/api/face-records${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  getByUser: async (userId) => {
    const response = await safeFetch(`${API_URL}/api/face-records/${userId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Monthly Work Days API
export const workDaysAPI = {
  get: async (year, month) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (month) params.append('month', month);
    const query = params.toString();
    const response = await safeFetch(`${API_URL}/api/work-days${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  getAll: async (year) => {
    const response = await safeFetch(`${API_URL}/api/work-days/all?year=${year}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  set: async (year, month, totalWorkDays, notes) => {
    const response = await safeFetch(`${API_URL}/api/work-days`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ year, month, total_work_days: totalWorkDays, notes })
    });
    return handleResponse(response);
  },
  delete: async (year, month) => {
    const response = await safeFetch(`${API_URL}/api/work-days/${year}/${month}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Password Management API
export const passwordAPI = {
  changeOwn: async (currentPassword, newPassword) => {
    const response = await safeFetch(`${API_URL}/api/users/change-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
    });
    return handleResponse(response);
  },
  adminReset: async (userId, newPassword) => {
    const response = await safeFetch(`${API_URL}/api/users/${userId}/reset-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ new_password: newPassword })
    });
    return handleResponse(response);
  }
};


// Backup API (Admin)
export const backupAPI = {
  download: async () => {
    const response = await fetch(`${API_URL}/api/backup/download`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Download failed');
    return response.blob();
  },
  prepare: async () => {
    const response = await safeFetch(`${API_URL}/api/backup/prepare`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  getPrepareStatus: async () => {
    const response = await safeFetch(`${API_URL}/api/backup/prepare-status`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  downloadFile: async () => {
    const response = await fetch(`${API_URL}/api/backup/download-file`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Download failed');
    const expected = Number(response.headers.get('content-length') || 0);
    const blob = await response.blob();
    if (expected && blob.size !== expected) throw new Error('Fail terpotong semasa muat turun');
    return blob;
  },
  restore: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('session_token');
    const response = await fetch(`${API_URL}/api/backup/restore`, {
      method: 'POST',
      headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      body: formData
    });
    // Read body eagerly for reliable parsing
    let text = '';
    try { text = await response.text(); } catch { /* body consumed */ }
    return parseResponse({ ok: response.ok, status: response.status, text });
  },
  getCollections: async () => {
    const response = await safeFetch(`${API_URL}/api/backup/collections`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  syncFromProduction: async (email, password, includeLocations) => {
    const result = await safeFetch(`${API_URL}/api/backup/sync-from-production`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ email, password, include_locations: includeLocations })
    });
    return parseResponse(result);
  },
  getSyncStatus: async () => {
    const result = await safeFetch(`${API_URL}/api/backup/sync-status`, {
      headers: getAuthHeaders()
    });
    return parseResponse(result);
  }
};

// Attendance Override API (Admin)
export const attendanceOverrideAPI = {
  override: async (attendanceId, data) => {
    const response = await safeFetch(`${API_URL}/api/attendance/${attendanceId}/override`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  manualEntry: async (staffId, date, clockIn, clockOut, notes) => {
    const params = new URLSearchParams({ staff_id: staffId, date, clock_in: clockIn });
    if (clockOut) params.append('clock_out', clockOut);
    if (notes) params.append('notes', notes);
    const response = await safeFetch(`${API_URL}/api/attendance/manual-entry?${params}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Update Users API - add email update
export const usersAPIExtended = {
  ...usersAPI,
  updateEmail: async (userId, email) => {
    const response = await safeFetch(`${API_URL}/api/users/${userId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ email })
    });
    return handleResponse(response);
  }
};



// Leaderboard API
export const leaderboardAPI = {
  getBranchLeaderboard: async (period = 'monthly', year, month) => {
    const params = new URLSearchParams({ period });
    if (year) params.append('year', year);
    if (month) params.append('month', month);
    const response = await safeFetch(`${API_URL}/api/leaderboard/branches?${params}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Crosscheck from DB Schedules API
export const crosscheckAPI = {
  fromSchedules: async (startDate, endDate, staffId, branchId) => {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (staffId) params.append('staff_id', staffId);
    if (branchId) params.append('branch_id', branchId);
    const response = await safeFetch(`${API_URL}/api/crosscheck/from-schedules?${params}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};
